import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { appConfig as defaultAppConfig } from '../app-config.js';
import { shouldAllowResearcher } from '../inference/runtime.js';
import { App } from '../tool/app.jsx';
import { resolveResearcherView } from '../tool/enabled-features.jsx';
import { armConfig } from '../../shared/experiment-arms.js';
import { pickEnrollmentClient } from './enrollment-client.js';
import { TelemetryEvent } from '../../shared/telemetry-events.js';
import { buildQualtricsUrl, qualtricsParamsFromSession } from './qualtrics.js';
import {
  clearSession,
  loadSession,
  PARTICIPANT_STORAGE_KEY,
  resolveEntryState,
  resolveIdentity,
  saveSession,
} from './participant-session.js';

function screenShell(title, body, action) {
  return (
    <div
      className="app-bg"
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}
    >
      <main
        role="status"
        style={{
          width: 'min(440px, 100%)',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 8,
          padding: 24,
          color: 'var(--ink)',
          boxShadow: '0 24px 60px oklch(25% 0.02 260 / 0.14)',
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 8 }}>
          Privacy Meter Study
        </div>
        <h1 style={{ fontSize: 22, lineHeight: 1.2, margin: '0 0 10px', fontWeight: 650 }}>
          {title}
        </h1>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.55 }}>
          {body}
        </p>
        {action && <div style={{ marginTop: 18 }}>{action}</div>}
      </main>
    </div>
  );
}

function ResetButton({ onReset }) {
  return (
    <button
      type="button"
      onClick={onReset}
      style={{
        border: '1px solid var(--line)',
        background: 'var(--paper-2)',
        color: 'var(--ink)',
        borderRadius: 8,
        padding: '8px 12px',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 13,
      }}
    >
      Reset local study lock
    </button>
  );
}

function BlockedScreen({ reason, researcherRequested, onReset }) {
  const body =
    {
      invalid:
        'The Prolific participant identifier in this link is not valid. Please return to Prolific and reopen the study link.',
      missing:
        'Open the study from Prolific so your participant identifier can be attached before the chat starts.',
    }[reason] ??
    'Open the study from Prolific so your participant identifier can be attached before the chat starts.';
  return screenShell(
    'Participant link required',
    body,
    researcherRequested ? <ResetButton onReset={onReset} /> : null
  );
}

function ConflictScreen({ session, params, researcherRequested, onReset }) {
  return screenShell(
    'Study session already locked',
    `This browser is locked to Prolific ID ${session.prolificId}. This link contains ${params.prolificId}.`,
    researcherRequested ? <ResetButton onReset={onReset} /> : null
  );
}

function EnrollingScreen() {
  return screenShell('Enrolling participant', 'Hold on while the study session is created.');
}

function EnrollErrorScreen({ error, researcherRequested, onRetry, onReset }) {
  return screenShell(
    'Enrollment failed',
    error || 'The study session could not be created. Please retry before continuing.',
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <button
        type="button"
        onClick={onRetry}
        style={{
          border: 'none',
          background: 'var(--indigo)',
          color: 'var(--paper)',
          borderRadius: 8,
          padding: '8px 12px',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 13,
        }}
      >
        Retry enrollment
      </button>
      {researcherRequested && <ResetButton onReset={onReset} />}
    </div>
  );
}

function NotLiveSessionScreen({ researcherRequested, onReset }) {
  return screenShell(
    'Session could not be verified',
    'Return to Prolific and reopen the study from your active Prolific session. This link cannot be used directly.',
    researcherRequested ? <ResetButton onReset={onReset} /> : null
  );
}

// The final leg of Prolific -> App -> Qualtrics -> Prolific. Rendered once
// the participant taps Finish in the tool (see tool/app.jsx's header Finish
// button + confirm sheet) — never automatically: there is no redirect timer
// or limit that jumps here on its own, only the explicit onFinish callback
// setting session.finishedAt (see ParticipantGate's handleFinish below).
function CompleteScreen({ session, researcherRequested, onReset, telemetry }) {
  const qualtricsUrl = buildQualtricsUrl(
    session?.qualtricsBaseUrl,
    qualtricsParamsFromSession(session)
  );

  // A null qualtricsUrl means a participant just finished the entire session
  // and hit a dead end (see the fallback message below) with nothing but a
  // client-side render to show for it. Without this, a researcher has no way
  // to notice a stranded participant short of the participant reporting it.
  useEffect(() => {
    if (qualtricsUrl) return;
    telemetry?.recordEvent?.(TelemetryEvent.HANDOFF_UNAVAILABLE, {
      experiment: session?.experiment ?? null,
      arm: session?.arm ?? null,
    });
  }, [qualtricsUrl, telemetry, session?.experiment, session?.arm]);

  const action = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
      {qualtricsUrl ? (
        <a
          href={qualtricsUrl}
          style={{
            display: 'inline-block',
            textDecoration: 'none',
            border: 'none',
            background: 'var(--indigo)',
            color: 'var(--paper)',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 13,
          }}
        >
          Continue to the survey
        </a>
      ) : (
        // The experiment's survey URL is missing from
        // backend.study.qualtricsUrlMap, a researcher configuration gap, not a participant
        // error. Responses up to this point are already recorded; only the handoff link is missing.
        <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          The survey link is unavailable right now. Your responses have been recorded — please
          contact the researcher to finish up.
        </span>
      )}
      {researcherRequested && <ResetButton onReset={onReset} />}
    </div>
  );
  return screenShell(
    'Thanks for taking part',
    'Your session is complete and your responses have been recorded. Continue to the final survey to finish the study.',
    action
  );
}

function ParticipantApp({ session, telemetry, onFinish, appConfig }) {
  const config = armConfig(session?.arm);
  return (
    <App
      enabledFeatures={config?.features ?? []}
      researcherAllowed={false}
      sessionToken={session?.sessionToken ?? null}
      enrolledAt={session?.enrolledAt ?? null}
      timeLimitMs={session?.timeLimitMs ?? 0}
      // Mid-experiment feature reveal is a report-experiment condition only
      // (see shared/experiment-arms.js) — whatif arms (F2, banner) always
      // reveal immediately regardless of what the server-configured delay
      // happens to be for this run.
      revealDelayMs={config?.experiment === 'report' ? (session?.revealDelayMs ?? 0) : 0}
      // The report gate (block other UI + hint until the participant
      // opens their arm's feature) is a report-experiment condition only,
      // same scoping as revealDelayMs above — whatif arms never gate.
      reportGateDelayMs={
        config?.experiment === 'report'
          ? (session?.reportGateDelayMs ?? session?.reportIntervalMs ?? 0)
          : 0
      }
      reportIntervalMs={
        config?.experiment === 'report'
          ? (session?.reportGateDelayMs ?? session?.reportIntervalMs ?? 0)
          : 0
      }
      // The report f4 arm starts hotspots off so a mid-experiment reveal
      // doesn't paint every message red at once; the participant flips
      // them on via the header Hotspots toggle. Every other arm keeps the
      // historical default-on — this expression computes it explicitly
      // (App's own initialHotspotsOn default only covers the researcher path,
      // which doesn't render through here).
      initialHotspotsOn={!config?.features.includes('F4')}
      initialHintTiming={config?.hintTiming ?? 'word'}
      initialF2Variant={config?.f2Variant ?? 'soft'}
      // No arm requests client-local scoring anymore (D2 word/sentence both
      // hit the live draft endpoint), so draftMode is left unset here and
      // falls through to App's own 'provider' default. PrebakedDraftInferenceClient
      // still exists for scripted/demo use — see frontend/inference/client.js.
      showBanner={Boolean(config?.banner)}
      telemetry={telemetry}
      appConfig={appConfig}
      onFinish={onFinish}
    />
  );
}
function ResearcherApp({ pathname, telemetry, appConfig }) {
  const view = resolveResearcherView(pathname);
  return (
    <App
      enabledFeatures={view.features}
      showBanner={view.banner}
      researcherAllowed
      telemetry={telemetry}
      appConfig={appConfig}
    />
  );
}

export function ParticipantGate({ telemetry, appConfig = defaultAppConfig } = {}) {
  const getLocation = () =>
    typeof window === 'undefined' ? { search: '', pathname: '/' } : window.location;
  const location = getLocation();
  // A `?arm=` live-qa override (see resolveIdentity) is honored only when
  // no "real" participant is already locked under the base storage key.
  const hasBaseLock =
    typeof window === 'undefined'
      ? false
      : Boolean(loadSession(window.localStorage, PARTICIPANT_STORAGE_KEY));
  const identity = useMemo(
    () => resolveIdentity(location.search, { hasBaseLock }),
    [location.search, hasBaseLock]
  );
  const [session, setSession] = useState(() =>
    typeof window === 'undefined' ? null : loadSession(window.localStorage, identity.storageKey)
  );
  const [enrollError, setEnrollError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const enrollmentPromise = useRef(null);
  const enrollmentKey = useRef(null);

  const entry = useMemo(
    () =>
      resolveEntryState({
        search: identity.search,
        session,
        allowResearcher: shouldAllowResearcher(appConfig),
      }),
    [appConfig, identity, session]
  );

  const resetLock = () => {
    if (typeof window !== 'undefined') clearSession(window.localStorage, identity.storageKey);
    setSession(null);
    setEnrollError(null);
    enrollmentPromise.current = null;
    enrollmentKey.current = null;
  };

  // The only path to CompleteScreen: tool/app.jsx's Finish confirm calls this
  // (via App's onFinish prop) once the participant explicitly confirms. There
  // is no timer or limit that calls this on its own — see AppShell's
  // confirmFinish, which is itself gated behind the confirm sheet.
  // finishedAt is persisted (not just held in React state) so a reload after
  // finishing lands back on CompleteScreen rather than re-opening the chat.
  // The updater below only computes next state; persistence happens in the
  // effect further down, keyed on session?.finishedAt. Keeping the updater
  // pure (no localStorage write inside it) matters because React — under
  // StrictMode especially — may invoke a setState updater more than once per
  // commit, which would otherwise double-write to localStorage.
  const handleFinish = (reason = 'manual') => {
    const finishedAt = new Date().toISOString();
    setSession((current) => (current ? { ...current, finishedAt, finishReason: reason } : current));
  };

  useEffect(() => {
    if (!session?.finishedAt || typeof window === 'undefined') return;
    saveSession(window.localStorage, session, identity.storageKey);
  }, [session, identity]);

  useEffect(() => {
    if (entry.kind !== 'enroll' || enrollError) return;

    const key = JSON.stringify({ search: identity.search, params: entry.params });
    if (enrollmentKey.current !== key) {
      enrollmentKey.current = key;
      enrollmentPromise.current = null;
    }
    if (!enrollmentPromise.current) {
      const client = pickEnrollmentClient(identity.search, { appConfig });
      enrollmentPromise.current = client.enroll(entry.params);
    }

    let active = true;
    enrollmentPromise.current
      .then((record) => {
        if (!active) return;
        const next = {
          prolificId: record.prolificId,
          studyId: entry.params.studyId,
          sessionId: entry.params.sessionId,
          participantId: record.participantId,
          sessionToken: record.sessionToken ?? null,
          enrolledAt: record.enrolledAt ?? new Date().toISOString(),
          timeLimitMs: record.timeLimitMs ?? 0,
          revealDelayMs: record.revealDelayMs ?? 0,
          reportGateDelayMs: record.reportGateDelayMs ?? record.reportIntervalMs ?? 0,
          reportIntervalMs: record.reportGateDelayMs ?? record.reportIntervalMs ?? 0,
          mode: record.status,
          experiment: record.experiment,
          arm: record.arm,
          // App -> Qualtrics handoff base URL (real: backend.study.qualtricsUrlMap;
          // mock: enrollment-client's local placeholder), before Prolific/study params
          // Prolific/study params are appended. Named qualtricsBaseUrl (not
          // qualtricsUrl) to stay distinct from CompleteScreen's local
          // `qualtricsUrl`, which is buildQualtricsUrl's final output. Null
          // when the experiment has no mapped survey — CompleteScreen falls
          // back to a "contact the researcher" message in that case.
          qualtricsBaseUrl: record.qualtricsBaseUrl ?? null,
        };
        if (typeof window !== 'undefined')
          saveSession(window.localStorage, next, identity.storageKey);
        setSession(next);
      })
      .catch((err) => {
        if (!active) return;
        setEnrollError({
          message: err instanceof Error ? err.message : String(err),
          code: err?.code ?? null,
        });
        enrollmentPromise.current = null;
      });

    return () => {
      active = false;
    };
  }, [appConfig, entry, enrollError, identity, retryNonce]);

  useLayoutEffect(() => {
    if (entry.kind === 'resume' && session?.participantId && session?.experiment && session?.arm) {
      telemetry?.setContext?.({
        participantId: session.participantId,
        experiment: session.experiment,
        arm: session.arm,
        participantSessionToken: session.sessionToken ?? null,
      });
      return;
    }
    telemetry?.setContext?.({
      participantId: null,
      experiment: null,
      arm: null,
      participantSessionToken: null,
    });
  }, [entry.kind, session, telemetry]);

  if (entry.kind === 'resume') {
    if (entry.session?.finishedAt) {
      return (
        <CompleteScreen
          session={entry.session}
          researcherRequested={entry.researcherRequested}
          onReset={resetLock}
          telemetry={telemetry}
        />
      );
    }
    return (
      <ParticipantApp
        session={entry.session}
        telemetry={telemetry}
        appConfig={appConfig}
        onFinish={handleFinish}
      />
    );
  }
  if (entry.kind === 'researcher')
    return (
      <ResearcherApp pathname={location.pathname} telemetry={telemetry} appConfig={appConfig} />
    );
  if (entry.kind === 'blocked') {
    return (
      <BlockedScreen
        reason={entry.reason}
        researcherRequested={entry.researcherRequested}
        onReset={resetLock}
      />
    );
  }
  if (entry.kind === 'conflict') {
    return (
      <ConflictScreen
        session={entry.session}
        params={entry.params}
        researcherRequested={entry.researcherRequested}
        onReset={resetLock}
      />
    );
  }
  if (entry.kind === 'enroll' && enrollError) {
    if (enrollError.code === 'not_live_session') {
      return (
        <NotLiveSessionScreen researcherRequested={entry.researcherRequested} onReset={resetLock} />
      );
    }
    return (
      <EnrollErrorScreen
        error={enrollError.message}
        researcherRequested={entry.researcherRequested}
        onReset={resetLock}
        onRetry={() => {
          setEnrollError(null);
          enrollmentPromise.current = null;
          enrollmentKey.current = null;
          setRetryNonce((n) => n + 1);
        }}
      />
    );
  }
  return <EnrollingScreen />;
}
